const CONTROL_HEIGHT = 56;
const DROPDOWN_VISUAL_SIZE = 36;
const TEXT_PADDING_X = 16;
const TEXT_PADDING_Y = (CONTROL_HEIGHT - 24) / 2;

/** Shared shape and states for every editable form control. */
export const FORM_CONTROL = {
  radius: "12px",
  pillRadius: "999px",
  minHeight: CONTROL_HEIGHT,
  dropdownVisualSize: DROPDOWN_VISUAL_SIZE,
  dropdownVisualBorderWidth: 1.5,
  dropdownInset: (CONTROL_HEIGHT - DROPDOWN_VISUAL_SIZE) / 2,
  toggleMinHeight: 52,
  fontSize: 16,
  textPaddingX: TEXT_PADDING_X,
  textPaddingY: TEXT_PADDING_Y,
  textPadding: `${TEXT_PADDING_Y}px ${TEXT_PADDING_X}px`,
  multilineMaxRows: 4,
  surface: "#1B1E28",
  hoverSurface: "#20232E",
  border: "rgba(255,255,255,0.16)",
  hoverBorder: "rgba(255,255,255,0.32)",
  focus: "#D8A363",
  error: "#EF4444",
  text: "#F5F4F2",
  placeholder: "#B3B3B3",
} as const;

/** An inset ring preserves fractional thickness, including over avatar images. */
export const dropdownVisualBorderSx = {
  "--fable-visual-border-color": "rgba(255,255,255,0.78)",
  position: "relative",
  border: 0,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    boxShadow: `inset 0 0 0 ${FORM_CONTROL.dropdownVisualBorderWidth}px var(--fable-visual-border-color)`,
    pointerEvents: "none",
    zIndex: 1,
  },
} as const;

/** Leading visuals keep a circular border box even in narrow flex layouts. */
export const circularControlVisualSx = (size: number) => ({
  width: size,
  height: size,
  minWidth: size,
  minHeight: size,
  maxWidth: size,
  maxHeight: size,
  aspectRatio: "1 / 1",
  boxSizing: "border-box",
  flex: "0 0 auto",
  borderRadius: "50%",
  overflow: "hidden",
  "& img": { width: "100%", height: "100%", objectFit: "cover" },
} as const);

/** Closed dropdowns use smaller visuals than their expanded option rows. */
export const dropdownInputVisualSx = circularControlVisualSx(FORM_CONTROL.dropdownVisualSize);

/** Keep illustrated icons proportional when their menu frame shrinks in an input. */
export const dropdownInputArtworkSx = (optionSize: number) => ({
  ...dropdownInputVisualSx,
  "& > :not(img)": {
    transform: `scale(${FORM_CONTROL.dropdownVisualSize / optionSize})`,
  },
} as const);

export const outlinedControlStyles = {
  minHeight: FORM_CONTROL.minHeight,
  borderRadius: FORM_CONTROL.radius,
  backgroundColor: FORM_CONTROL.surface,
  color: FORM_CONTROL.text,
  fontSize: FORM_CONTROL.fontSize,
  lineHeight: 1.5,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: 2,
    borderColor: FORM_CONTROL.border,
  },
  "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: FORM_CONTROL.hoverBorder,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: FORM_CONTROL.focus,
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: FORM_CONTROL.error,
  },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.42)" },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.10)",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "rgba(255,255,255,0.42)",
  },
  "& input, & textarea": { caretColor: "#fff" },
  "& input::placeholder, & textarea::placeholder": {
    color: FORM_CONTROL.placeholder,
    opacity: 1,
  },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 1000px ${FORM_CONTROL.surface} inset`,
    WebkitTextFillColor: FORM_CONTROL.text,
    borderRadius: "inherit",
  },
} as const;

/** Names and descriptions use the same single-line size and text treatment. */
export const textInputRootStyles = {
  ...outlinedControlStyles,
  fontWeight: 400,
  color: "#E0E0E0",
  WebkitTextFillColor: "#E0E0E0",
  "&.MuiInputBase-multiline": { padding: FORM_CONTROL.textPadding },
  "& input::placeholder, & textarea::placeholder": {
    color: FORM_CONTROL.placeholder,
    WebkitTextFillColor: FORM_CONTROL.placeholder,
    opacity: 1,
  },
  "& textarea": {
    overscrollBehavior: "contain",
  },
} as const;

export const textInputContentStyles = {
  padding: FORM_CONTROL.textPadding,
  "&.MuiInputBase-inputMultiline": { padding: 0 },
} as const;

/** For buttons that open a picker but occupy a form field. */
export const formPickerSurfaceSx = {
  minHeight: FORM_CONTROL.minHeight,
  borderRadius: FORM_CONTROL.radius,
  backgroundColor: FORM_CONTROL.surface,
  color: FORM_CONTROL.text,
  border: `2px solid ${FORM_CONTROL.border}`,
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: FORM_CONTROL.hoverSurface,
    borderColor: FORM_CONTROL.hoverBorder,
  },
  "&.Mui-focusVisible, &:focus-visible": { borderColor: FORM_CONTROL.focus },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.42)",
    borderColor: "rgba(255,255,255,0.10)",
  },
} as const;
